import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { isPhoneNumber } from '@/helpers';
import { useApiMutation } from '@/modules/common/hooks/useApiMutation';

import {
  EventOrder,
  SendOrderSmsPayload,
  SendSmsPayload,
} from '../../../../types';
import { SendSmsDialogSchema } from '../../../../validations';
import { ConfirmSendSmsDialog } from '../../participants-tab/components/confirm-send-sms-dialog';

interface SendSmsDialogProps {
  isOpen: boolean;
  eventId?: string;
  orderIds: string[];
  orders: EventOrder[];
  onOpenChange: (open: boolean) => void;
}

export type SendSmsDialogValues = z.infer<typeof SendSmsDialogSchema>;

export function SendSmsDialog({
  isOpen,
  onOpenChange,
  eventId,
  orderIds,
  orders,
}: SendSmsDialogProps) {
  const [isConfirmSendSmsDialogOpen, setIsConfirmSendSmsDialogOpen] =
    useState(false);

  const selectedOrders = orders.filter((order) => orderIds.includes(order.id));
  const validPhoneOrderIds = selectedOrders
    .filter((order) => isPhoneNumber(order.phoneNumber))
    .map((order) => order.id);

  const form = useForm<SendSmsDialogValues>({
    resolver: zodResolver(SendSmsDialogSchema),
    defaultValues: {
      message: '',
    },
  });

  const sendSmsMutation = useApiMutation<SendOrderSmsPayload>(
    `/events/${eventId}/orders/send-sms`,
    {
      method: 'POST',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const sendSms = async () => {
    try {
      const data = form.getValues();

      await sendSmsMutation.mutateAsync({
        message: data.message,
        orderIds: validPhoneOrderIds,
      });

      setIsConfirmSendSmsDialogOpen(false);
      toast.success('SMS sent successfully!');

      onOpenChange(false);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleFormSubmit = () => {
    setIsConfirmSendSmsDialogOpen(true);
  };

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={isOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Send SMS</DialogTitle>
            <DialogDescription>
              Send an SMS message to selected participants.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1 w-full">
            <p className="text-sm text-muted-foreground">
              You are trying to send an SMS to the following participants:
            </p>
            <ul className="list-disc list-inside max-h-40 h-max overflow-y-auto w-full">
              {selectedOrders.map((order) => (
                <li key={order.id} className="text-sm text-muted-foreground">
                  {`${order.firstName} ${order.lastName} (${order.phoneNumber})`}
                </li>
              ))}
            </ul>
          </div>

          <Form {...form}>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                form.handleSubmit(handleFormSubmit)(event);
              }}
            >
              <div className="flex flex-col max-w-6xl">
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Message</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Max 500 characters"
                          maxLength={500}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="mt-6 gap-4">
                <DialogClose asChild>
                  <Button
                    variant="outline"
                    className="w-[130px]"
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  className="w-[130px]"
                  data-testid="save-button"
                  isLoading={sendSmsMutation.isPending}
                  disabled={
                    !form.formState.isValid || !validPhoneOrderIds.length
                  }
                >
                  Send SMS
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {isConfirmSendSmsDialogOpen && (
        <ConfirmSendSmsDialog
          isOpen={isConfirmSendSmsDialogOpen}
          onSubmit={sendSms}
          onOpenChange={setIsConfirmSendSmsDialogOpen}
        />
      )}
    </>
  );
}
